// @flow

import { useState, useEffect, useRef } from "react";
import { useSubmissionContext } from "../../context/SubmissionContext";
import { useSchemaContext } from "../../context/SchemaContext";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";
import { useExerciseContext } from "../../context/ExerciseContext";
import ConfirmButton from "../../shared/components/ConfirmButton";
import Tooltip from "@kea-mod/common/tooltip";
import { FaRedo } from "react-icons/fa";
import { getReviewedMap, getReviewedMapFromSubmissions, setReviewed } from "../../shared/reviewStatusStore";
import { getAssessmentCache } from "../../shared/assessmentCache";
import { displayNameOfUser, setRevisionUser } from "../../shared/revisionUserStore";
import AssessmentDataService from "../../axios/services/assessment.service";
import { SubmissionStatisticsComponent, ruleOfFeedback } from "./SubmissionStatisticsComponent";
import { StaleListNotice } from "../StaleListNotice";

const dateOptions = {
  weekday: "short",
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
} as any;

// How many assessments are collected before the table is redrawn while they are loaded.
const PUBLISH_EVERY = 10;

// The two publication states carry texts of very different length; without a fixed
// width their column would ripple down the table.
const STATUS_WIDTH = "9rem";
// A tag and a button do not share Bulma's height, so it is set for both.
const CONTROL_HEIGHT = "2rem";

export const ExerciseSubmissionListComponent = () => {
  const { t, i18n } = useTranslation();
  let { exerciseId } = useParams(); // exerciseId
  const SubmissionContext = useSubmissionContext();
  const exerciseContext = useExerciseContext();
  const SchemaContext = useSchemaContext();
  const navigate = useNavigate();
  // Starts from the session cache, so returning to this list does not refetch the
  // assessment of every submission again (they only change through explicit actions).
  const [assessmentMap, setAssessmentMap] = useState(() => getAssessmentCache(exerciseId)) as any;
  const [refresh, setRefresh] = useState(true);
  const [refresh2, setRefresh2] = useState(true);
  const [sum_totalscore, setSum_totalscore] = useState(0.0);
  const [sum_maxscore, setSum_maxscore] = useState(0);
  const [schema, setSchema] = useState({}) as any;
  const [currentSubmissionsForExercise, setCurrentSubmissionsForExercise] = useState([]) as any;
  const [pendingAssessments, setPendingAssessments] = useState<any[]>([]);
  // Progress of a bulk start, shown next to the button. Null while none is running.
  const [bulkProgress, setBulkProgress] = useState<any>(null);
  const pollCountRef = useRef(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState("created");
  const [sortAscending, setSortAscending] = useState(true);
  const [publicationFilter, setPublicationFilter] = useState("all");
  // Assessments arrive one by one, so the overview has to say that its numbers are still growing.
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  // Submissions the polling gave up on. Without this the row silently falls back to its old
  // value after three minutes, and nothing says that no one is watching it any more.
  const [unresponsiveAssessments, setUnresponsiveAssessments] = useState<any[]>([]);
  // Set from the overview: shows only the submissions affected by one grader rule.
  const [ruleFilter, setRuleFilter] = useState("");
  const [reviewedMap, setReviewedMap] = useState<any>(() => getReviewedMap(exerciseId));

  const [reviewFailed, setReviewFailed] = useState(false);
  const [publishRunning, setPublishRunning] = useState(false);
  const [publishMessage, setPublishMessage] = useState("");

  const toggleReviewed = async (submissionExerciseId: any, reviewed: boolean) => {
    const stored = await setReviewed(exerciseId, submissionExerciseId, reviewed);
    setReviewedMap(getReviewedMap(exerciseId));
    setReviewFailed(!stored);
  };

  // Only submissions that were marked as corrected and are not published yet are
  // candidates, so nothing that has not been looked at can go out to the students.
  const getPublishCandidates = () => {
    return currentSubmissionsForExercise.filter((submission: any) => {
      if (reviewedMap[submission.id] !== true) return false;
      const entry = assessmentMap[submission.id];
      if (entry === undefined || !("last_complete_assessment" in entry)) return false;
      const assessment = entry["last_complete_assessment"];
      return assessment.id !== undefined && assessment.isRevised !== true;
    });
  };

  // Every submission whose result is already out. Withdrawing puts it back to pending
  // without touching the score, so the correction can be revised before publishing again.
  const getWithdrawCandidates = () => {
    return currentSubmissionsForExercise.filter((submission: any) => {
      const entry = assessmentMap[submission.id];
      if (entry === undefined || !("last_complete_assessment" in entry)) return false;
      const assessment = entry["last_complete_assessment"];
      return assessment.id !== undefined && assessment.isRevised === true;
    });
  };

  // The cached assessment of the table holds display values (converted scores), so the
  // current assessment is fetched again and written back with the publication flag only.
  const setPublicationFlag = async (candidates: any[], isRevised: boolean) => {
    if (candidates.length === 0) return;

    setPublishRunning(true);
    setPublishMessage("");
    let published = 0;
    let failed = 0;
    const publishedIds: any[] = [];

    for (const submission of candidates) {
      const assessmentId = assessmentMap[submission.id]["last_complete_assessment"].id;
      try {
        const response = (await AssessmentDataService.get(assessmentId)) as any;
        const current = response?.data?.data ?? response?.data;

        // Without a score to write back the update would overwrite the result with
        // empty values, so such an entry is counted as failed and left untouched.
        if (current === undefined || current.totalscore === undefined || current.maxTotalScore === undefined) {
          failed = failed + 1;
          continue;
        }

        await AssessmentDataService.update(assessmentId, {
          comment: current.comment === null || current.comment === "" ? " " : current.comment,
          totalscore: current.totalscore,
          maxTotalScore: current.maxTotalScore,
          isRevised: isRevised,
        });
        published = published + 1;
        publishedIds.push(submission.id);
      } catch (error) {
        failed = failed + 1;
      }
    }

    setPublishRunning(false);
    const doneKey = isRevised ? "publishReviewedDone" : "withdrawPublishedDone";
    const partlyKey = isRevised ? "publishReviewedPartly" : "withdrawPublishedPartly";
    setPublishMessage(
      failed === 0
        ? t(doneKey, { number: published })
        : t(partlyKey, { number: published, failed: failed }),
    );

    // The published rows are updated right away instead of waiting for a status run
    // over the whole list, which would take minutes on a large exercise.
    publishedIds.forEach((submissionId: any) => {
      const entry = assessmentMap[submissionId];
      if (entry !== undefined && "last_complete_assessment" in entry) {
        entry["last_complete_assessment"].isRevised = isRevised;
      }
    });
    setAssessmentMap(assessmentMap);
    setRefresh(!refresh);
  };

  const publishReviewedAssessments = async () => setPublicationFlag(getPublishCandidates(), true);

  const withdrawPublishedAssessments = async () => setPublicationFlag(getWithdrawCandidates(), false);

  useEffect(() => {}, [refresh]);

  useEffect(() => {
    getSubmissionsForExercise();
    SchemaContext.updateSchemas();
    exerciseContext.all();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSubmissionsForExercise = async () => {
    let allSubmissionsForExercise = await SubmissionContext.getAllSubmissionsForExercise(
      parseInt(exerciseId as string),
    );
    setCurrentSubmissionsForExercise(allSubmissionsForExercise);
  };

  useEffect(() => {
    const submissionsForExercise = SubmissionContext.submissionsForExercise;
    if (submissionsForExercise === undefined) return;
    setCurrentSubmissionsForExercise(submissionsForExercise);
    // The stored flag wins over whatever this browser remembers from an earlier visit.
    setReviewedMap(getReviewedMapFromSubmissions(exerciseId, submissionsForExercise));

    if (submissionsForExercise.length !== 0) {
      checkforCompleteAssessment();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SubmissionContext.submissionsForExercise]);

  const getSchema = async () => {
    var schemaId = currentSubmissionsForExercise[0].exercise.schemaId;
    for (var i = 0; i < SchemaContext.schemas.length; i++) {
      if (schemaId === SchemaContext.schemas[i].id) {
        setSchema(SchemaContext.schemas[i]);
        break;
      }
    }
    setRefresh(!refresh);
  };

  // Without the guard a new run starts every few seconds while the previous one is
  // still working through the list, so the runs pile up and each of them requests the
  // whole list again.
  const checkRunningRef = useRef(false);

  // Called without arguments the status of every submission that is not already settled
  // in the session cache is fetched. While single assessments are running only those are asked
  // for, so a running assessment costs one request instead of one per submission.
  const checkforCompleteAssessment = async (submissionIds?: any[], forceAll?: boolean) => {
    if (checkRunningRef.current) return;
    checkRunningRef.current = true;
    // Only a full pass makes the numbers incomplete. The poll asks for a handful of running
    // assessments, and marking that as loading made the notice blink every three seconds.
    const isFullPass = submissionIds === undefined;
    if (isFullPass) setAssessmentsLoading(true);

    // Complete assessments stay in the session cache and are not fetched again; only
    // missing and still-incomplete entries are. The refresh button forces a full pass.
    const isSettled = (submission: any) => {
      const entry = assessmentMap[submission.id];
      if (entry === undefined || entry["status"] !== false) return false;
      // The maximum comes from the exercise and can have changed while the entry sat in
      // the cache, so an entry that still carries the old one is fetched again.
      const maxOfExercise = parseFloat(submission.exercise?.maxTotalScore);
      const maxInEntry = parseFloat(entry["last_complete_assessment"]?.maxTotalScore);
      if (!isNaN(maxOfExercise) && !isNaN(maxInEntry) && maxOfExercise !== maxInEntry) return false;
      return true;
    };
    const targets =
      submissionIds === undefined
        ? currentSubmissionsForExercise.filter((submission: any) => forceAll === true || !isSettled(submission))
        : currentSubmissionsForExercise.filter((submission: any) => submissionIds.includes(submission.id));

    try {
      var bar = assessmentMap as any;
      // The map keeps its identity because it is the session cache itself, so only these
      // two switches redraw the table. They have to be flipped from the value at the time
      // of the call: while an assessment is polled, this runs from an interval that was
      // built once, and a captured value would be written again unchanged from the second
      // round on. The results would then arrive but stay invisible until a page reload.
      const publish = () => {
        setAssessmentMap(bar);
        setRefresh((value: boolean) => !value);
        setRefresh2((value: boolean) => !value);
      };

      for (var i = 0; i < targets.length; i++) {
        var submission = targets[i];

        let ex_type = submission.exercise.type;
        var booli = true;
        let assessment = (await SubmissionContext.getAssessment(submission.id)) as any;

        // A failed request says nothing about the submission, so the entry is left as it is.
        if (assessment.requestFailed === true) continue;

        let as = { totalscore: "-", maxTotalScore: "-" } as any;

        if (!("message" in assessment && assessment["message"] === "incomplete")) {
          as["maxTotalScore"] = assessment.maxTotalScore;
        }

        as["totalscore"] = "-";

        if (assessment.status === "complete") {
          as = assessment;
          booli = false;
          // The maximum belongs to the exercise, so the list shows that value and not the
          // one stored with the single assessment.
          const maxOfExercise = submission.exercise?.maxTotalScore;
          if (maxOfExercise !== undefined && maxOfExercise !== null) {
            as["maxTotalScore"] = maxOfExercise;
          }
          if (ex_type === "creation") {
            as["totalscore"] = parseFloat((as["totalscore"] * as["maxTotalScore"]).toFixed(2));
          }
        }

        // A handover that failed is an end state just like a finished one. Without this
        // the row would keep saying that the assessment is running and the list would
        // wait for a result that never comes.
        const failed = assessment.status === "broken";
        if (failed) {
          booli = false;
        }

        bar[submission.id] = { last_complete_assessment: as, status: booli, failed: failed };

        // The assessments are fetched one submission at a time, so on an exercise with a
        // few hundred of them the first pass runs for minutes. Publishing in batches lets
        // the table and the overview fill up while it runs instead of staying empty until
        // the very end. It costs no additional request, and every tenth entry keeps the
        // number of redraws small enough not to slow the pass down.
        if ((i + 1) % PUBLISH_EVERY === 0) {
          publish();
        }
      }
      publish();
    } finally {
      checkRunningRef.current = false;
      if (isFullPass) setAssessmentsLoading(false);
    }
  };

  useEffect(() => {
    computeSumScores();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  useEffect(() => {
    if (currentSubmissionsForExercise.length !== 0) {
      getSchema();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh2]);

  const computeSumScores = () => {
    const keys = Object.keys(assessmentMap);
    let totalmax: any = 0.0;
    let maxmax: any = 0.0;
    let counter = 0;
    let countermax = 0;
    for (let i = 0; i < keys.length; i++) {
      let key = keys[i];
      let obj = assessmentMap[key]["last_complete_assessment"];

      if (obj.maxTotalScore === "-" || obj.maxTotalScore === undefined) {
        continue;
      }
      maxmax = maxmax + obj.maxTotalScore;
      countermax = countermax + 1;
      if (!("gradings" in obj)) {
        continue;
      }
      counter = counter + 1;

      totalmax = totalmax + parseFloat(obj.totalscore.toFixed(2));
    }

    if (counter === 0 || countermax === 0) {
      totalmax = "-";
      maxmax = "-";
    } else {
      totalmax = parseFloat((totalmax / counter).toFixed(2));
      maxmax = parseFloat((maxmax / countermax).toFixed(2));
    }

    setSum_totalscore(totalmax);
    setSum_maxscore(maxmax);
  };

  const showSubmissions = (submission: any) => {
    setRevisionUser(submission.id, displayNameOfUser(submission.user));
    navigate("/subpreview/" + submission.id);
  };

  // A failed assessment is loaded but carries only a message, so it has no id to link to.
  const hatErgebnis = (submission: any) => {
    const eintrag = assessmentMap[submission.id];
    if (eintrag === undefined || eintrag["status"] !== false) return false;
    return eintrag["last_complete_assessment"]?.id !== undefined;
  };

  const showAssessment = async (submission: any) => {
    localStorage.setItem("originStudentTable", ""); //empty string corresponds to false (boolean) in OrientationBar.jsx
    let submissionId = submission.id;
    let assessment = assessmentMap[submissionId]["last_complete_assessment"];
    for (var i = 0; i < currentSubmissionsForExercise.length; i++) {
      if (currentSubmissionsForExercise[i].id === submission.id) {
        setRevisionUser(submission.id, displayNameOfUser(currentSubmissionsForExercise[i].user));
      }
    }

    if (submission.type === "creation") {
      navigate("/assessments-mc/" + submissionId + "/" + exerciseId + "/" + assessment.id);
    } else {
      navigate("/assessments-mu/" + submissionId + "/" + exerciseId + "/" + assessment.id);
    }
  };

  const manualAssessment = async (submission: any) => {
    localStorage.setItem("originStudentTable", ""); //empty string corresponds to false (boolean) in OrientationBar.jsx
    for (var i = 0; i < currentSubmissionsForExercise.length; i++) {
      if (currentSubmissionsForExercise[i].id === submission.id) {
        setRevisionUser(submission.id, displayNameOfUser(currentSubmissionsForExercise[i].user));
      }
    }

    if (submission.id in assessmentMap && Object.keys(assessmentMap[submission.id]).length > 0) {
      navigate("/manual-assessment/" + submission.id + "/" + exerciseId);
    }
  };

  const StartAssessmentForEntry = (id: any) => {
    delete assessmentMap[id];
    SubmissionContext.startAssessment(id);
    // Poll the assessment status so the result shows up without a manual refresh.
    pollCountRef.current = 0;
    setUnresponsiveAssessments((prev) => prev.filter((entry: any) => entry !== id));
    setPendingAssessments((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setRefresh(!refresh);
  };

  // Assessing all submissions no longer uses the backend bulk endpoint. That endpoint
  // starts every job at once (`Promise.all` over all submissions in
  // assessment.controller.js), while the database pool allows twenty connections at a
  // time. Whatever does not get one within the acquire timeout is dropped without a
  // message: a run over 364 submissions finished 282 of them, whereas starting the same
  // rows one by one always worked. So the proven single-start endpoint is used here as
  // well, a few at a time, and failed requests are retried.
  const StartAssessmentForAll = async (_id: any) => {
    const submissionIds = currentSubmissionsForExercise.map((submission: any) => submission.id);
    submissionIds.forEach((submissionId: any) => delete assessmentMap[submissionId]);
    pollCountRef.current = 0;
    setUnresponsiveAssessments([]);
    setPendingAssessments(submissionIds);
    setRefresh(!refresh);

    const queue = [...submissionIds];
    const total = queue.length;
    let started = 0;
    let failed = 0;
    setBulkProgress({ started, failed, total });

    const worker = async () => {
      while (queue.length > 0) {
        const submissionId = queue.shift();
        if (submissionId === undefined) return;
        let ok = false;
        for (let attempt = 0; attempt < 3 && !ok; attempt++) {
          ok = await SubmissionContext.startAssessment(submissionId);
        }
        if (ok) started++;
        else failed++;
        setBulkProgress({ started, failed, total });
      }
    };

    // Four requests in flight keep the grading pipeline busy without the burst that
    // makes the backend drop jobs.
    try {
      await Promise.all([worker(), worker(), worker(), worker()]);
    } finally {
      setBulkProgress({ started, failed, total, done: true });
    }
  };

  // While assessments are running, re-check their status every few seconds (read-only).
  useEffect(() => {
    if (pendingAssessments.length === 0) return;
    const interval = setInterval(() => {
      pollCountRef.current += 1;
      if (pollCountRef.current > 60) {
        // Stop polling after ~3 minutes; the manual refresh button still works.
        setUnresponsiveAssessments(pendingAssessments);
        setPendingAssessments([]);
        return;
      }
      checkforCompleteAssessment(pendingAssessments);
    }, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAssessments]);

  // Drop finished assessments from the pending list once their status is complete.
  useEffect(() => {
    if (pendingAssessments.length === 0) return;
    const stillPending = pendingAssessments.filter(
      (id: any) => !(id in assessmentMap) || assessmentMap[id]["status"] !== false,
    );
    if (stillPending.length !== pendingAssessments.length) {
      setPendingAssessments(stillPending);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh2]);

  const displayName = (submission: any) => displayNameOfUser(submission.user);

  const publicationState = (submission: any) => {
    const entry = assessmentMap[submission.id];
    if (entry && entry.last_complete_assessment && entry.last_complete_assessment.isRevised !== undefined) {
      return entry.last_complete_assessment.isRevised ? "published" : "pending";
    }
    return "none";
  };

  const scoreValue = (submission: any) => {
    const entry = assessmentMap[submission.id];
    if (entry && entry.last_complete_assessment && typeof entry.last_complete_assessment.totalscore === "number") {
      return entry.last_complete_assessment.totalscore;
    }
    return undefined;
  };

  // True when the last complete assessment of this submission contains the given rule.
  const hasRule = (submission: any, rule: string) => {
    const entry = assessmentMap[submission.id];
    if (entry === undefined || !("last_complete_assessment" in entry)) return false;
    const gradings = entry["last_complete_assessment"].gradings ?? [];
    return gradings.some((grading: any) =>
      (grading.feedbacks ?? []).some(
        (feedback: any) => feedback.valuation === "NEGATIVE" && ruleOfFeedback(feedback.content) === rule,
      ),
    );
  };

  const visibleSubmissions = () => {
    let entries = currentSubmissionsForExercise.slice();
    const term = searchTerm.trim().toLowerCase();
    if (term !== "") {
      entries = entries.filter((submission: any) => displayName(submission).toLowerCase().includes(term));
    }
    if (publicationFilter !== "all") {
      entries = entries.filter((submission: any) => publicationState(submission) === publicationFilter);
    }
    if (ruleFilter !== "") {
      entries = entries.filter((submission: any) => hasRule(submission, ruleFilter));
    }
    const direction = sortAscending ? 1 : -1;
    const stateOrder = { published: 0, pending: 1, none: 2 } as any;
    entries.sort((a: any, b: any) => {
      if (sortKey === "user") {
        return direction * displayName(a).localeCompare(displayName(b), undefined, { sensitivity: "base" });
      }
      if (sortKey === "score") {
        const scoreA = scoreValue(a);
        const scoreB = scoreValue(b);
        if (scoreA === undefined && scoreB === undefined) return 0;
        if (scoreA === undefined) return 1;
        if (scoreB === undefined) return -1;
        return direction * (scoreA - scoreB);
      }
      if (sortKey === "revised") {
        return direction * (stateOrder[publicationState(a)] - stateOrder[publicationState(b)]);
      }
      return direction * (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    });
    return entries;
  };

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      setSortAscending(!sortAscending);
    } else {
      setSortKey(key);
      setSortAscending(true);
    }
  };

  const sortableHeader = (key: string, label: any) => (
    <span style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => toggleSort(key)}>
      {label} {sortKey === key ? sortAscending ? "▲" : "▼" : <span className="has-text-grey-light">⇅</span>}
    </span>
  );

  function getSubmissionTableItem(submission: any) {
    const name = displayName(submission);
    return (
      <tr key={"Submission" + submission.id}>
        <th>{name}</th>
        <th>{new Date(submission.createdAt).toLocaleDateString(i18n.language, dateOptions)}</th>
        <td className="has-text-center has-text-weight-bold">
          {submission.id in assessmentMap && assessmentMap[submission.id]["failed"] === true ? (
            <span className="tag is-danger is-light" title={t("assessmentFailedHint")}>
              {t("assessmentFailed")}
            </span>
          ) : pendingAssessments.includes(submission.id) ? (
            <span className="tag is-info is-light">{t("assessmentRunning")}</span>
          ) : unresponsiveAssessments.includes(submission.id) ? (
            <span className="tag is-warning is-light" title={t("assessmentUnresponsiveHint")}>
              {t("assessmentUnresponsive")}
            </span>
          ) : submission.id in assessmentMap &&
            "last_complete_assessment" in assessmentMap[submission.id] &&
            assessmentMap[submission.id]["last_complete_assessment"].maxTotalScore !== undefined ? (
            assessmentMap[submission.id]["last_complete_assessment"].totalscore +
            "/" +
            assessmentMap[submission.id]["last_complete_assessment"].maxTotalScore
          ) : (
            "-/-"
          )}
        </td>
        <td style={{ minWidth: "17rem" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.25rem" }}>
          <button
            className="button is-small is-info"
            onClick={() => showSubmissions(submission)}
          >
            {t("submissions")}
          </button>
          {/* Same name as the row: the dialog named the internal login, which cannot be checked
              against the person the click was meant for. */}
          <ConfirmButton
            id={submission.id}
            name={displayName(submission)}
            proceedAction={StartAssessmentForEntry}
            question={`startAssessmentSingleQuestion`}
            explanation={`startAssessmentSingleExplanation`}
            color="button is-small is-primary"
            buttonLabel={`startAssessment`}
          />

          <button
            className="button is-small is-success"
            disabled={
              submission.id in assessmentMap && "status" in assessmentMap[submission.id]
                ? assessmentMap[submission.id]["status"]
                : true
            }
            onClick={() => manualAssessment(submission)}
          >
            {t("ManualAssessmentAction")}
          </button>
          <span
            className={hatErgebnis(submission) ? "" : "tooltip has-tooltip-multiline has-tooltip-top"}
            data-tooltip={hatErgebnis(submission) ? undefined : t("noAssessmentOnButton")}
          >
            <button
              className="button is-small is-info"
              disabled={!hatErgebnis(submission)}
              onClick={() => showAssessment(submission)}
            >
              {t("showAssessment")}
            </button>
          </span>
          </div>
        </td>
        <td className="has-text-center has-text-weight-bold">
          {submission.id in assessmentMap &&
          "last_complete_assessment" in assessmentMap[submission.id] &&
          assessmentMap[submission.id]["last_complete_assessment"].isRevised !== undefined ? (
            assessmentMap[submission.id]["last_complete_assessment"].isRevised ? (
              <div style={{ display: "grid", gap: "0.25rem", minWidth: STATUS_WIDTH, justifyItems: "stretch" }}>
                <span className="tag is-success" style={{ width: "100%", height: CONTROL_HEIGHT, justifyContent: "center" }}>
                  {t("published")}
                </span>
                {/* Dieselbe Schwelle wie die Sammelaktion: Auch die einzelne Rücknahme nimmt einer
                    studierenden Person ihr Ergebnis aus der Ansicht, ohne dass sie davon erfährt. */}
                <ConfirmButton
                  id={submission.id}
                  name={displayName(submission)}
                  proceedAction={() => setPublicationFlag([submission], false)}
                  question={`withdrawPublishedQuestion`}
                  explanation={`withdrawPublishedExplanation`}
                  color="is-small is-warning is-light"
                  style={{ width: "100%", height: CONTROL_HEIGHT, margin: 0 }}
                  disabled={publishRunning}
                  title={t("withdrawPublishedExplanation")}
                  buttonLabel={`withdrawOne`}
                />
              </div>
            ) : (
              <div style={{ display: "grid", gap: "0.25rem", minWidth: STATUS_WIDTH, justifyItems: "stretch" }}>
                <span className="tag is-warning" style={{ width: "100%", height: CONTROL_HEIGHT, justifyContent: "center" }}>
                  {t("publicationPending")}
                </span>
                <button
                  className="button is-small is-success is-light"
                  style={{ width: "100%", height: CONTROL_HEIGHT, margin: 0 }}
                  disabled={publishRunning || reviewedMap[submission.id] !== true}
                  title={reviewedMap[submission.id] === true ? t("publishOneExplanation") : t("publishOneBlocked")}
                  onClick={() => setPublicationFlag([submission], true)}
                >
                  {t("publishOne")}
                </button>
              </div>
            )
          ) : (
            ""
          )}
        </td>
        <td className="has-text-center">
          <label className="checkbox">
            <input
              type="checkbox"
              checked={reviewedMap[submission.id] === true}
              title={reviewedMap[submission.id] === true ? t("reviewedDone") : t("reviewedOpen")}
              onChange={(event) => toggleReviewed(submission.id, event.target.checked)}
            />
          </label>
        </td>
      </tr>
    );
  }

  function getSubmissionTable() {
    const visible = visibleSubmissions();
    return (
      // Only the table scrolls sideways. Search and filters used to sit inside the scrolling
      // frame, so on a narrow window they wandered out of the picture together with the columns,
      // which is exactly where one reaches for them.
      <div>
        {/* Wraps instead of forcing a minimum width: search field plus four filter buttons need
            about 1180 pixels in one line, which pushed the whole page sideways on a narrow
            window and cut off everything to the right of it. */}
        <div className="field is-grouped is-grouped-multiline mb-3">
          <div className="control is-expanded">
            <input
              className="input"
              type="search"
              placeholder={t("searchSubmissionsPlaceholder")}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
          <div className="control">
            <div className="buttons has-addons mb-0">
              <button
                className={"button mb-0" + (publicationFilter === "all" ? " is-info is-selected" : "")}
                onClick={() => setPublicationFilter("all")}
              >
                {t("filterAllSubmissions")}
              </button>
              <button
                className={"button mb-0" + (publicationFilter === "published" ? " is-info is-selected" : "")}
                onClick={() => setPublicationFilter("published")}
              >
                {t("published")}
              </button>
              <button
                className={"button mb-0" + (publicationFilter === "pending" ? " is-info is-selected" : "")}
                onClick={() => setPublicationFilter("pending")}
              >
                {t("publicationPending")}
              </button>
              <button
                className={"button mb-0" + (publicationFilter === "none" ? " is-info is-selected" : "")}
                onClick={() => setPublicationFilter("none")}
              >
                {t("filterNoAssessment")}
              </button>
            </div>
          </div>
        </div>
        {(searchTerm.trim() !== "" || publicationFilter !== "all" || ruleFilter !== "") && (
          <p className="help mb-2">
            {visible.length} / {currentSubmissionsForExercise.length} {t("submissions")}
            {ruleFilter !== "" && (
              <>
                {" · "}
                {t("ruleFilterActive")}
                {": "}
                <span className="has-text-weight-semibold">{ruleFilter}</span>{" "}
                <button
                  type="button"
                  className="button is-ghost is-small p-0"
                  style={{ height: "auto" }}
                  onClick={() => setRuleFilter("")}
                >
                  {t("ruleFilterClear")}
                </button>
              </>
            )}
          </p>
        )}
        <div className="table-container">
          <table className="table is-striped is-narrow is-hoverable is-fullwidth submission-table">
          <thead className="is-italic">
            <tr>
              <td>{sortableHeader("user", t("user"))}</td>
              <td>{sortableHeader("created", t("created"))}</td>
              <td>
                {sortableHeader("score", t("totalScore") + "/" + t("maxTotalScore"))}
                <Tooltip
                  icon="circle-info"
                  class="icon tooltip no-border is-white has-tooltip-multiline has-tooltip-top"
                  content={t("submissionScoreTooltip")}
                ></Tooltip>
              </td>
              <td>{t("options")}</td>
              <td>{sortableHeader("revised", t("Revised"))}</td>
              <td>
                {t("reviewedHeader")}
                <Tooltip
                  icon="circle-info"
                  class="icon tooltip no-border is-white has-tooltip-multiline has-tooltip-top"
                  content={t("reviewedTooltip")}
                ></Tooltip>
              </td>
            </tr>
          </thead>
          <tbody>
            {visible.map((submission: any) => {
              return getSubmissionTableItem(submission);
            })}
            <tr>
              <td></td>
              <td></td>
              <td className="has-text-center has-text-weight-bold">
                {sum_totalscore}/{sum_maxscore} {t("avgScores")}
              </td>
              <td></td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  function getEmptyMessageSubmission() {
    if (SubmissionContext.submissionsLoadFailed) {
      return <div className="notification is-danger is-light">{t("listLoadFailed")}</div>;
    }
    return <p className="is-size-7">{t("noSubmissions")} </p>;
  }

  const publishCandidateCount = getPublishCandidates().length;
  const withdrawCandidateCount = getWithdrawCandidates().length;

  let renderSubmissionTable = (
    <div className="card my-2">
      <div className="card-content">
        <div className="columns">
          {/* A flex item does not shrink below the minimum width of its content unless told
              to. Without this the wide table pushed the column past its own row, the page
              grew sideways and everything to the right was cut off, while the table's own
              scroll frame never came into play. */}
          <div className="column has-text-left" style={{ minWidth: 0 }}>
            <StaleListNotice
              loadFailed={SubmissionContext.submissionsLoadFailed}
              hasEntries={currentSubmissionsForExercise.length > 0}
            />
            {currentSubmissionsForExercise.length > 0 && getSubmissionTable()}
            {currentSubmissionsForExercise.length === 0 && getEmptyMessageSubmission()}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="section">
      <div className="card mt-6">
        <header className="card-header">
          <p className="card-header-title">{t("submissions")}</p>
        </header>
        <div className="card-content">
          <div className="content">
            <div className="columns">
              <div className="column has-text-weight-light is-italic">
                {t("allSubmissionsForExercise")}
                {t("currentSchema") + schema.name + ". "}
                {t("explainButton")}
                <br />
              </div>
            </div>
            <div className="columns">
              <div className="column">
                <ConfirmButton
                  id={exerciseId}
                  name={localStorage.getItem("exerciseName")}
                  proceedAction={StartAssessmentForAll}
                  question={`startAssessmentQuestion`}
                  explanation={`startAssessmentExplanation`}
                  color="button is-primary mt-3 ml-0"
                  buttonLabel={`assessAllSubmissions`}
                />
                {publishCandidateCount > 0 && !publishRunning ? (
                  <ConfirmButton
                    id={exerciseId}
                    name={t("publishReviewedSelection", { number: publishCandidateCount })}
                    proceedAction={publishReviewedAssessments}
                    question={`publishReviewedQuestion`}
                    explanation={`publishReviewedExplanation`}
                    color="button is-success mt-3 ml-3"
                    buttonLabel={`publishReviewed`}
                  />
                ) : (
                  <button className="button is-success mt-3 ml-3" disabled>
                    {publishRunning ? t("publishReviewedRunning") : t("publishReviewed")}
                  </button>
                )}
                <Tooltip
                  icon="circle-info"
                  class="mt-3 icon tooltip no-border is-white has-tooltip-multiline has-tooltip-top"
                  content={t("publishReviewedTooltip")}
                ></Tooltip>
                {withdrawCandidateCount > 0 && !publishRunning ? (
                  <ConfirmButton
                    id={exerciseId}
                    name={t("withdrawPublishedSelection", { count: withdrawCandidateCount })}
                    proceedAction={withdrawPublishedAssessments}
                    question={`withdrawPublishedQuestion`}
                    explanation={`withdrawPublishedExplanation`}
                    color="button is-warning mt-3 ml-3"
                    buttonLabel={`withdrawPublished`}
                  />
                ) : (
                  <button className="button is-warning mt-3 ml-3" disabled>
                    {t("withdrawPublished")}
                  </button>
                )}
                <Tooltip
                  icon="circle-info"
                  class="mt-3 icon tooltip no-border is-white has-tooltip-multiline has-tooltip-top"
                  content={t("withdrawPublishedExplanation")}
                ></Tooltip>
                {publishMessage !== "" && <p className="is-size-7 mt-2">{publishMessage}</p>}
                {reviewFailed && <p className="is-size-7 mt-2 has-text-danger">{t("reviewFlagFailed")}</p>}
                {/* Starting a few hundred assessments takes a while, so the button says
                    how far it got. Without this it looks as if nothing happens. */}
                {bulkProgress !== null && (
                  <p className="is-size-7 mt-2">
                    {!bulkProgress.done ? (
                      <>
                        {/* Counts what has been handled, not what succeeded, otherwise the number
                            stays at zero while every attempt fails. */}
                        {t("bulkStartProgress", {
                          processed: bulkProgress.started + bulkProgress.failed,
                          total: bulkProgress.total,
                        })}
                        {bulkProgress.failed > 0 && (
                          <span className="has-text-danger">
                            {" "}
                            {t("bulkStartFailed", { failed: bulkProgress.failed })}
                          </span>
                        )}
                      </>
                    ) : bulkProgress.started === 0 ? (
                      <span className="has-text-danger">{t("bulkStartNone", { total: bulkProgress.total })}</span>
                    ) : bulkProgress.failed > 0 ? (
                      <>
                        {t("bulkStartPartial", { started: bulkProgress.started, total: bulkProgress.total })}
                        <span className="has-text-danger">
                          {" "}
                          {t("bulkStartFailed", { failed: bulkProgress.failed })}
                        </span>
                      </>
                    ) : (
                      t("bulkStartDone", { total: bulkProgress.total })
                    )}
                  </p>
                )}
              </div>
              <div className="column has-text-right has-text-bottom">
                <Tooltip
                  icon="circle-info"
                  class="mt-3 icon tooltip no-border is-white has-tooltip-multiline has-tooltip-top"
                  content={t("CheckfornewFeedback")}
                ></Tooltip>
                {/* The forced pass over all submissions takes minutes and changes nothing
                    visible when the data is unchanged, so the button itself has to show
                    that it is working. */}
                <div
                  className={"button mt-3 has-text-right is-primary" + (assessmentsLoading ? " is-loading" : "")}
                  onClick={() => checkforCompleteAssessment(undefined, true)}
                >
                  <FaRedo />
                </div>
                <br />
              </div>
            </div>
            {currentSubmissionsForExercise.length > 0 && (
              <SubmissionStatisticsComponent
                submissions={currentSubmissionsForExercise}
                assessmentMap={assessmentMap}
                loading={assessmentsLoading}
                activeRule={ruleFilter}
                onSelectRule={(rule: string) => setRuleFilter(ruleFilter === rule ? "" : rule)}
              />
            )}
            {renderSubmissionTable}
          </div>
        </div>
      </div>
    </div>
  );
};
